/**
 * visual-helpers.ts — 数据驱动占位件的颜色算法 helper（hash → hue）
 *
 * 逐字搬自原型 design/Novel-admin/src/primitives.jsx §「颜色算法」段，收口一处：
 * - coverBg(hue)    书籍封面渐变：linear-gradient(160deg, oklch(0.6 0.14 H), oklch(0.4 0.1 (H+50)%360))
 * - avatarBg(hue)   头像渐变：    linear-gradient(135deg, oklch(0.6 0.14 H), oklch(0.4 0.1 (H+80)%360))
 * - avatarHue(seed) 默认 seed→hue：取字符串第 idx(默认 4)个字符 charCode × mult(默认 47) % 360，
 *                   与 users / feedback 旧实现一致。
 *
 * Why oklch 字面量而非 token：这是「确定性数据着色」算法（hue 是数据派生量，非主题色），
 * 渐变两端由 hue 连续生成，无法落到离散 token 档位；与原型 1:1 保真，不可替换。
 */

/** 书籍封面渐变背景。hue 来自 book.coverHue（0~359）。 */
export function coverBg(hue: number): string {
  const h = Number(hue) || 0;
  return `linear-gradient(160deg, oklch(0.6 0.14 ${h}), oklch(0.4 0.1 ${(h + 50) % 360}))`;
}

/** 头像渐变背景。hue 显式传入或由 avatarHue(seed) 派生。 */
export function avatarBg(hue: number): string {
  const h = Number(hue) || 0;
  return `linear-gradient(135deg, oklch(0.6 0.14 ${h}), oklch(0.4 0.1 ${(h + 80) % 360}))`;
}

/** avatarHue 的可选参数：idx=取第几个字符（默认 4），mult=乘数（默认 47）。 */
interface AvatarHueOptions {
  idx?: number;
  mult?: number;
}

/**
 * 默认 seed→hue：取 seed 第 idx 个字符的 charCode（缺省回退首字符）× mult % 360。
 * 与 users / feedback 旧实现逐字等价。
 */
export function avatarHue(
  seed: string | null | undefined,
  { idx = 4, mult = 47 }: AvatarHueOptions = {},
): number {
  const s = String(seed || '');
  const code = s.charCodeAt(idx) || s.charCodeAt(0) || 0;
  return (code * mult) % 360;
}
